import type {
  CrmManualSelectionRow,
  ExternalManualSelectionRow,
} from '@modules/reports/cma-reports/step-transaction/types';
import { encodeSelectionKey } from '@modules/reports/cma-reports/step-transaction/utils/selectionKey';

const SAME_DAY_MAX_DISTANCE_GAP_METERS = 100;
const SAME_DAY_TOLERANCE_DAYS = 1;
const SAME_DAY_PRICE_TOLERANCE_RATIO = 0.05;
const EXACT_PRICE_MAX_DISTANCE_GAP_METERS = 50;
const EXACT_PRICE_WINDOW_DAYS = 120;
const EXACT_PRICE_TOLERANCE = 1;
const EXACT_PRICE_SURFACE_TOLERANCE_RATIO = 0.25;
const DAY_IN_MS = 86_400_000;

export type SaleFacts = {
  distance: number;
  time: number;
  price: number;
  postcode: string | null;
  mainType: string | null;
  livingSurface: number | null;
};

type SaleLike = {
  distance_in_meters: number | null | undefined;
  transaction?: {
    signature_date?: string | null;
    purchase_price?: number | null;
    postcode?: string | null;
    property_main_type?: string | null;
    living_surface?: number | null;
  } | null;
};

const normalizeText = (value: string | null | undefined) => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
};

export const getSaleFacts = (row: SaleLike): SaleFacts | null => {
  const signatureDate = row.transaction?.signature_date;
  const time = signatureDate != null ? Date.parse(signatureDate) : Number.NaN;
  const price = row.transaction?.purchase_price ?? Number.NaN;
  const distance = row.distance_in_meters ?? Number.NaN;
  if (
    !Number.isFinite(time) ||
    !Number.isFinite(price) ||
    price <= 0 ||
    !Number.isFinite(distance)
  ) {
    return null;
  }
  const livingSurface = row.transaction?.living_surface;
  return {
    distance,
    time,
    price,
    postcode: normalizeText(row.transaction?.postcode),
    mainType: normalizeText(row.transaction?.property_main_type),
    livingSurface:
      livingSurface != null && livingSurface > 0 ? livingSurface : null,
  };
};

const sameWhenKnown = (a: string | null, b: string | null) =>
  a == null || b == null || a === b;

const closeWhenKnown = (
  a: number | null,
  b: number | null,
  toleranceRatio: number,
) =>
  a == null || b == null || Math.abs(a - b) <= toleranceRatio * Math.max(a, b);

export const isSameSale = (a: SaleFacts, b: SaleFacts) => {
  if (
    !sameWhenKnown(a.postcode, b.postcode) ||
    !sameWhenKnown(a.mainType, b.mainType)
  ) {
    return false;
  }
  const distanceGap = Math.abs(a.distance - b.distance);
  const days = Math.abs(a.time - b.time) / DAY_IN_MS;
  const priceGap = Math.abs(a.price - b.price);
  const sameDaySale =
    distanceGap <= SAME_DAY_MAX_DISTANCE_GAP_METERS &&
    days <= SAME_DAY_TOLERANCE_DAYS &&
    priceGap <= SAME_DAY_PRICE_TOLERANCE_RATIO * Math.max(a.price, b.price);
  const exactPriceSale =
    distanceGap <= EXACT_PRICE_MAX_DISTANCE_GAP_METERS &&
    days <= EXACT_PRICE_WINDOW_DAYS &&
    priceGap <= EXACT_PRICE_TOLERANCE &&
    closeWhenKnown(
      a.livingSurface,
      b.livingSurface,
      EXACT_PRICE_SURFACE_TOLERANCE_RATIO,
    );
  return sameDaySale || exactPriceSale;
};

type IndexedExternal = {
  row: ExternalManualSelectionRow;
  index: number;
  facts: SaleFacts;
};

const firstIndexAtOrAbove = (rows: IndexedExternal[], distance: number) => {
  let low = 0;
  let high = rows.length;
  while (low < high) {
    const mid = (low + high) >> 1;
    if (rows[mid].facts.distance < distance) {
      low = mid + 1;
    } else {
      high = mid;
    }
  }
  return low;
};

export const dedupeManualSelectionRows = (
  crmRows: CrmManualSelectionRow[],
  externalRows: ExternalManualSelectionRow[],
  savedKeys: ReadonlySet<string>,
): {
  crmRows: CrmManualSelectionRow[];
  externalRows: ExternalManualSelectionRow[];
} => {
  if (crmRows.length === 0 || externalRows.length === 0) {
    return { crmRows, externalRows };
  }
  const externalByDistance: IndexedExternal[] = externalRows
    .flatMap((row, index) => {
      const facts = getSaleFacts(row);
      return facts == null ? [] : [{ row, index, facts }];
    })
    .sort((a, b) => a.facts.distance - b.facts.distance);
  const pairedExternal = new Set<number>();
  const hiddenCrm = new Set<number>();
  const hiddenExternal = new Set<number>();

  crmRows.forEach((crmRow, crmIndex) => {
    const facts = getSaleFacts(crmRow);
    if (facts == null) {
      return;
    }
    const upperDistance = facts.distance + SAME_DAY_MAX_DISTANCE_GAP_METERS;
    for (
      let i = firstIndexAtOrAbove(
        externalByDistance,
        facts.distance - SAME_DAY_MAX_DISTANCE_GAP_METERS,
      );
      i < externalByDistance.length &&
      externalByDistance[i].facts.distance <= upperDistance;
      i += 1
    ) {
      const candidate = externalByDistance[i];
      if (
        pairedExternal.has(candidate.index) ||
        !isSameSale(facts, candidate.facts)
      ) {
        continue;
      }
      const crmSaved = savedKeys.has(
        encodeSelectionKey('crm', crmRow.transaction_id),
      );
      const externalSaved = savedKeys.has(
        encodeSelectionKey(
          candidate.row.source,
          candidate.row.transaction.external_id,
        ),
      );
      pairedExternal.add(candidate.index);
      if (crmSaved && externalSaved) {
        return;
      }
      if (externalSaved) {
        hiddenCrm.add(crmIndex);
      } else {
        hiddenExternal.add(candidate.index);
      }
      return;
    }
  });

  if (hiddenCrm.size === 0 && hiddenExternal.size === 0) {
    return { crmRows, externalRows };
  }
  return {
    crmRows: crmRows.filter((_, index) => !hiddenCrm.has(index)),
    externalRows: externalRows.filter((_, index) => !hiddenExternal.has(index)),
  };
};
